import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useServices } from "../../context/ServiceProvider";
import { Alert, Badge, Button, Table, Tag } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { ChevronDown, ChevronRight } from "lucide-react";
import CreateProjectObjectiveModal from "../project-objective/CreateProjectObjectiveModal";

/**
 * Reusable section component for displaying Project Objectives
 * @param {string} projectId - The project ID to fetch objectives for
 */
const ProjectObjectivesSection = ({
  projectId,
  projectName,
  startDate,
  endDate,
}) => {
  const { projectObjective } = useServices();
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState("");
  const [list, setList] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);

  async function load() {
    if (!projectId) return;
    setLoading(true);
    setError("");
    try {
      const data =
        await projectObjective.fetchProjectObjectivesByProjectId(projectId);
      setList(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.message || "Failed to load project objectives");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const handleAddSuccess = () => {
    setShowAddModal(false);
  };

  return (
    <>
      <Badge.Ribbon
        placement="start"
        style={{ display: loading === false ? "block" : "none" }}
        text={`#${list.length}`}
        color="blue"
      >
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="flex items-center justify-between p-6">
            <button
              type="button"
              onClick={() => setIsExpanded((v) => !v)}
              className="flex-1 flex items-start flex-col text-left"
            >
              <div className="text-lg font-semibold text-gray-900">
                Project Objectives
              </div>
              <div className="text-sm text-gray-600">
                List of project objectives for this project
              </div>
            </button>
            <div className="flex items-center gap-2">
              <Button
                type="primary"
                onClick={() => setShowAddModal(true)}
                size="small"
                icon={<PlusOutlined />}
              >
                Add
              </Button>
              <button
                type="button"
                onClick={() => setIsExpanded((v) => !v)}
                className="p-1"
              >
                {isExpanded ? (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-500" />
                )}
              </button>
            </div>
          </div>
          {isExpanded && (
            <div className="border-t border-gray-200 p-4">
              {error && (
                <div className="mb-2">
                  <Alert type="error" showIcon message={error} />
                </div>
              )}
              <Table
                loading={loading}
                dataSource={list}
                rowKey="Id"
                pagination={false}
                size="small"
                columns={[
                  {
                    title: "Name",
                    dataIndex: "Name",
                    key: "Name",
                    ellipsis: true,
                    render: (text, rec) => (
                      <Link
                        to={{ pathname: `/po/${rec.Id}` }}
                        state={{ crumbLabel: rec.Name }}
                        className="text-primary-600"
                      >
                        {text || rec.Id}
                      </Link>
                    ),
                  },
                  {
                    title: "Project",
                    dataIndex: ["Project__r", "Name"],
                    key: "Project",
                    width: "30%",
                    ellipsis: true,
                    render: (text, rec) => {
                      const project = rec.Project__r;
                      if (!project) return "-";
                      return (
                        <Link
                          to={{ pathname: `/p/${project.Id}` }}
                          state={{ crumbLabel: project.Name }}
                          className="text-primary-600"
                        >
                          {text || project.Name}
                        </Link>
                      );
                    },
                  },
                  {
                    title: "Status",
                    dataIndex: "Status__c",
                    key: "Status__c",
                    width: "15%",
                    render: (text, rec) => (
                      <Tag
                        color={
                          rec.Status__c === "Open"
                            ? "green"
                            : rec.Status__c === "Closed"
                              ? "red"
                              : "blue"
                        }
                      >
                        {text || rec.Status__c || "N/A"}
                      </Tag>
                    ),
                  },
                ]}
              />
            </div>
          )}
        </div>
      </Badge.Ribbon>
      <CreateProjectObjectiveModal
        visible={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleAddSuccess}
        initialValues={{ projectId, projectName, startDate, endDate }}
      />
    </>
  );
};

export default ProjectObjectivesSection;
